import { Spacing } from '@persona-id/design-tokens';
import { snakeCase } from 'lodash';
import { startCase } from 'lodash/fp';
import { Controller, FieldErrors, UseFormReturn } from 'react-hook-form';
import styled from 'styled-components';

import {
  DEFAULT_AI_MODEL,
  ExtractionPanelForm,
  MatchRequirement,
} from 'dashboard/components/ExtractionPanel/util';
import {
  ExtractionFieldType,
  ExtractionRequest,
  ExtractionType,
  GuidedExtractionRequest,
  MatchLevel,
} from 'dashboard/models/document';
import { ErrorText } from 'design-system/components/FormControl';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { Label } from 'design-system/components/Text';

const Section = styled.section`
  padding: ${Spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
`;

const StyledLabel = styled(Label)`
  margin-bottom: ${Spacing[2]};
  display: inline-block;
`;

// TODO (Documents): Migrate to PDS component for underline tooltip indicators when available

const FieldErrorText = ({ message }: { message?: string }) =>
  message ? (
    <>
      <Spacer height={0.5} />
      <ErrorText>{message}</ErrorText>
    </>
  ) : null;

export const guidedExtractionV2RequestToFormState = (
  extractionRequest: GuidedExtractionRequest,
): Partial<ExtractionPanelForm> => {
  let serializedFieldType = extractionRequest['field-type'];
  if (
    extractionRequest['field-type'] === ExtractionFieldType.String &&
    extractionRequest.version === '2.0' &&
    extractionRequest['ai-model']
  ) {
    serializedFieldType = ExtractionFieldType.Ai;
  }
  return {
    version: '2.0',
    fieldName: startCase(extractionRequest['field-name']),
    extractionType: extractionRequest['extraction-type'],
    fieldType: serializedFieldType,
    matchLevelMinimum: extractionRequest['match-level-minimum'],
  };
};

export const guidedFormStateToExtractionV2Request = (
  data: ExtractionPanelForm,
): ExtractionRequest => {
  let fieldType = data.fieldType;
  let aiModel = undefined;
  // The 'ai' field type is ONLY used in the front-end; this gets converted to a field type of 'string' and a non-null 'ai-model' in the back-end
  if (data.fieldType === ExtractionFieldType.Ai) {
    fieldType = ExtractionFieldType.String;
    aiModel = DEFAULT_AI_MODEL;
  }
  return {
    version: '2.0',
    'extraction-type': ExtractionType.Guided,
    'field-type': fieldType,
    'field-name': snakeCase(data.fieldName),
    'edit-distance-maximum': data.matchLevelMinimum === MatchLevel.Full ? 0 : 1,
    'match-level-minimum': data.matchLevelMinimum,
    'ai-model': aiModel,
  };
};

export const validateGuidedExtractionV2PanelForm = (
  data: ExtractionPanelForm,
  unconfigurableInquiryFieldNames: string[],
) => {
  const errors: FieldErrors<ExtractionPanelForm> = {};
  const errorRequired = { type: 'required', message: 'Required' };

  if (unconfigurableInquiryFieldNames.includes(snakeCase(data.fieldName))) {
    errors.fieldName = {
      type: 'type',
      message: 'The corresponding inquiry field is of an unconfigurable type',
    };
  }
  if (data?.fieldType == null) {
    errors.fieldType = errorRequired;
  }
  if (data?.matchLevelMinimum == null) {
    errors.matchLevelMinimum = errorRequired;
  }
  return errors;
};

interface GuidedExtractionPanelProps {
  formReturn: UseFormReturn<ExtractionPanelForm, object>;
  disabled?: boolean;
}

const GuidedExtractionV2Panel = ({ formReturn, disabled = false }: GuidedExtractionPanelProps) => {
  const { formState, control } = formReturn;
  const { errors } = formState;

  return (
    <Section style={{ paddingTop: 0 }}>
      <StyledLabel>Extraction type</StyledLabel>
      <Controller
        name='fieldType'
        control={control}
        render={({ field }) => (
          <Input
            value={startCase(field.value)}
            hasError={!!errors?.fieldName}
            disabled={disabled}
          />
        )}
      />
      <FieldErrorText message={errors?.fieldType?.message} />
      <Spacer height={1.5} />
      <StyledLabel>Minimum match requirement</StyledLabel>
      <Controller
        name='matchLevelMinimum'
        control={control}
        render={({ field }) => {
          let value;
          switch (field.value) {
            case MatchLevel.Full:
              value = startCase(MatchRequirement.Strict) + ' (Legacy)';
              break;
            case MatchLevel.Partial:
              value = startCase(MatchRequirement.Loose) + ' (Legacy)';
              break;
            case MatchLevel.None:
              value = startCase(field.value);
              break;
          }
          return <Input value={value} hasError={!!errors?.fieldName} disabled={disabled} />;
        }}
      />
      <FieldErrorText message={errors?.matchLevelMinimum?.message} />
    </Section>
  );
};

export default GuidedExtractionV2Panel;
